import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { clsx } from 'clsx';
import { FieldRenderer } from './FieldRenderer';
import { evaluateBlockingFields } from './fieldValidation';
import type {
  LineFormModalProps,
  ComputedRule,
  LineModalSectionConfig,
  LookupFieldConfig,
  FieldErrorMap,
} from './MasterDocument.types';
import type { ServerRuleConfig } from './template/MasterDocumentTemplate.types';
import { evaluateCondition, extractDependencyFields } from './fieldCondition';
import type { FieldCondition } from './fieldCondition';
import { buildDependentLookupProps, buildDependentsByParent } from './lookupFilters';
import type { DependentLookupProps } from './lookupFilters';
import { SerialPanel } from './SerialPanel';
import { useSerialPanel } from './SerialPanel/hooks';
import { useSerialPanelTranslation } from './SerialPanel/i18n';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useMasterDocumentTranslation } from './i18n';
import { FieldLoadingIndicator } from './components/loading';

// ─── Icons ─────────────────────────────────────────────────

function XIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
    </svg>
  );
}

// ─── Computed Rules Runner ─────────────────────────────────

function ComputedRulesRunner({
  rules,
  control,
  setValue,
}: {
  rules: ComputedRule[];
  control: ReturnType<typeof useForm>['control'];
  setValue: ReturnType<typeof useForm>['setValue'];
}) {
  // Watch all fields that any rule depends on
  const allWatchFields = [...new Set(rules.flatMap((r) => r.watch))];
  const watchedValues = useWatch({ control, name: allWatchFields });

  useEffect(() => {
    const formSnapshot: Record<string, unknown> = {};
    allWatchFields.forEach((field, idx) => {
      formSnapshot[field] = watchedValues[idx];
    });

    rules.forEach((rule) => {
      const result = rule.compute(formSnapshot);
      setValue(rule.targetField, result, { shouldDirty: false });
    });
  }, [watchedValues, rules, setValue, allWatchFields]);

  return null;
}

// ─── Form Values Watcher ───────────────────────────────────

function FormValuesWatcher({
  control,
  onChange,
}: {
  control: ReturnType<typeof useForm>['control'];
  onChange: (values: Record<string, unknown>) => void;
}) {
  const values = useWatch({ control });
  useEffect(() => {
    const t = setTimeout(() => onChange(values as Record<string, unknown>), 100);
    return () => clearTimeout(t);
  }, [values, onChange]);
  return null;
}

/**
 * Recalcula los campos que bloquean, para que los mensajes y los badges se apaguen a
 * medida que el usuario los resuelve tras un intento fallido.
 *
 * Va en un hijo, como `FormValuesWatcher`: si el `useWatch` de todos los valores viviera
 * en el modal, el modal entero se re-renderizaría en cada pulsación. Y solo se monta
 * DESPUÉS del primer intento fallido, así que antes no observa nada.
 */
function LineBlockingWatcher({
  control,
  calcular,
  onChange,
}: {
  control: ReturnType<typeof useForm>['control'];
  calcular: (values: Record<string, unknown>) => FieldErrorMap;
  onChange: (errors: FieldErrorMap) => void;
}) {
  const values = useWatch({ control });
  // La firma evita el bucle de render: `useWatch` devuelve un objeto nuevo en cada
  // render, así que sin comparar el CONTENIDO cada recálculo dispararía otro estado.
  const firmaPrevia = useRef<string | null>(null);
  useEffect(() => {
    const errores = calcular((values ?? {}) as Record<string, unknown>);
    const firma = JSON.stringify(
      Object.entries(errores).map(([k, v]) => [k, v.message]).sort(),
    );
    if (firma !== firmaPrevia.current) {
      firmaPrevia.current = firma;
      onChange(errores);
    }
  }, [values, calcular, onChange]);
  return null;
}

// ─── Server Rules Runner ───────────────────────────────────

function ServerRulesRunner({
  rules,
  control,
  setValue,
  onEvaluate,
  setLoadingFields,
}: {
  rules: ServerRuleConfig[];
  control: ReturnType<typeof useForm>['control'];
  setValue: ReturnType<typeof useForm>['setValue'];
  onEvaluate: (ruleId: string, formData: Record<string, unknown>) => Promise<Partial<Record<string, unknown>>>;
  setLoadingFields: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const allTriggerFields = useMemo(
    () => [...new Set(rules.flatMap((r) => r.triggerFields))],
    [rules],
  );
  const watched = useWatch({ control, name: allTriggerFields as [string, ...string[]] });

  const initialized = useRef(false);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const generations = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const isInit = !initialized.current;
    initialized.current = true;

    const snapshot: Record<string, unknown> = {};
    allTriggerFields.forEach((k, i) => { snapshot[k] = watched[i]; });

    rules.forEach((rule) => {
      if (isInit && (rule.triggerOn ?? 'change') !== 'always') return;
      if (rule.condition && !evaluateCondition(rule.condition as FieldCondition, snapshot)) return;

      const existing = timers.current.get(rule.ruleId);
      if (existing) clearTimeout(existing);

      const delay = rule.debounceMs ?? 300;
      const timer = setTimeout(async () => {
        const gen = (generations.current.get(rule.ruleId) ?? 0) + 1;
        generations.current.set(rule.ruleId, gen);

        setLoadingFields((prev) => {
          const next = new Set(prev);
          rule.targetFields.forEach((f) => next.add(f));
          return next;
        });

        try {
          const result = await onEvaluate(rule.ruleId, snapshot);
          if (generations.current.get(rule.ruleId) !== gen) return;
          Object.entries(result).forEach(([key, value]) => {
            setValue(key, value, { shouldDirty: false });
          });
        } catch {
          // Leave fields unchanged on error
        } finally {
          if (generations.current.get(rule.ruleId) === gen) {
            setLoadingFields((prev) => {
              const next = new Set(prev);
              rule.targetFields.forEach((f) => next.delete(f));
              return next;
            });
          }
        }

        timers.current.delete(rule.ruleId);
      }, delay);

      timers.current.set(rule.ruleId, timer);
    });
  }, [watched]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear all pending timers on unmount
  useEffect(() => {
    return () => {
      timers.current.forEach((t) => clearTimeout(t));
    };
  }, []);

  return null;
}

// ─── LineFormModal ──────────────────────────────────────────

/**
 * LineFormModal — modal deslizante para crear o editar una línea del documento.
 *
 * En escritorio se muestra como un diálogo centrado; en móvil se presenta
 * como una hoja inferior (bottom sheet). Gestiona el formulario internamente
 * con `react-hook-form` y ejecuta las reglas de cálculo automático mediante
 * `ComputedRulesRunner`.
 *
 * @see FieldRenderer — renderiza cada campo del formulario
 * @see ComputedRule — tipo para las reglas de campos calculados
 *
 * @example
 * ```tsx
 * <LineFormModal
 *   open={lineModal.open}
 *   onClose={() => setLineModal({ open: false, lineId: null })}
 *   onConfirm={(data) => handleLineSave(lineModal.lineId, data)}
 *   fields={lineFields}
 *   title="Nueva línea"
 *   confirmLabel="Agregar línea"
 * />
 * ```
 */
export function LineFormModal({
  open,
  onClose,
  onConfirm,
  onConfirmAndAddAnother,
  fields,
  initialData,
  computedRules,
  serverRules,
  onEvaluateServerRule,
  errors: externalErrors,
  busy = false,
  title,
  confirmLabel,
  cancelLabel,
  readOnly = false,
  docState,
  columns = 2,
  sections,
  widgetSlot,
  dynamicEntitiesSlot,
  onFormValuesChange,
  lookupConfigs,
  fetcher,
  headerValues,
  serialSupport,
  serialReadOnly,
  validateSerial,
  fetchAvailableSerials,
  className,
}: LineFormModalProps) {
  const { t } = useMasterDocumentTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('master_document.chrome.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('master_document.chrome.cancel');
  const sortedFields = [...fields].sort((a, b) => a.order - b.order);
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>('');
  /**
   * Campos que el guardián marcó en el último intento de guardar. Vacío ⇒ no hubo intento
   * fallido (o ya se resolvió). Alimenta a la vez el mensaje de cada campo y el badge de
   * su pestaña, así que las dos señales no pueden contradecirse.
   */
  const [erroresCliente, setErroresCliente] = useState<FieldErrorMap>({});
  const huboIntentoFallido = Object.keys(erroresCliente).length > 0;
  const [loadingFields, setLoadingFields] = useState<Set<string>>(new Set());

  // Collect all field keys referenced in field AND section conditions
  const conditionDeps = useMemo(() => {
    const keys = new Set<string>();
    sortedFields.forEach((f) => {
      if (f.visibleWhen) extractDependencyFields(f.visibleWhen as FieldCondition).forEach((k) => keys.add(k));
      if (f.hiddenWhen) extractDependencyFields(f.hiddenWhen as FieldCondition).forEach((k) => keys.add(k));
    });
    sections?.forEach((s) => {
      if (s.visibleWhen) extractDependencyFields(s.visibleWhen as FieldCondition).forEach((k) => keys.add(k));
      if (s.hiddenWhen) extractDependencyFields(s.hiddenWhen as FieldCondition).forEach((k) => keys.add(k));
    });
    return [...keys];
  }, [sortedFields, sections]);

  // Build default values from fields
  const defaultValues: Record<string, unknown> = {};
  fields.forEach((f) => {
    defaultValues[f.key] = initialData?.[f.key] ?? f.defaultValue ?? '';
  });

  const { control, setValue, reset, handleSubmit } = useForm({
    defaultValues,
  });

  // Watch only the fields referenced in conditions for reactive visibility
  const watchedConditionValues = useWatch({ control, name: conditionDeps as [string, ...string[]] });

  const conditionSnapshot = useMemo(() => {
    const snap: Record<string, unknown> = {};
    conditionDeps.forEach((key, i) => { snap[key] = watchedConditionValues[i]; });
    return snap;
  }, [conditionDeps, watchedConditionValues]);

  // ── Cascading lookups (filterDependencies) ──────────────
  const lineFieldKeys = useMemo(() => new Set(fields.map((f) => f.key)), [fields]);

  // Padres (form-keys) que son campos de la LÍNEA y deben observarse en vivo.
  // Los padres de CABECERA se resuelven contra `headerValues` (estables en el modal).
  const lineParentKeys = useMemo(() => {
    const keys = new Set<string>();
    fields.forEach((f) => {
      const fd = lookupConfigs?.[f.key]?.filterDependencies;
      if (fd) Object.values(fd).forEach((k) => { if (lineFieldKeys.has(k)) keys.add(k); });
    });
    return [...keys];
  }, [fields, lookupConfigs, lineFieldKeys]);

  const watchedParents = useWatch({
    control,
    name: (lineParentKeys.length ? lineParentKeys : ['__no_cascade__']) as [string, ...string[]],
  });

  const lineParentSnapshot = useMemo(() => {
    const snap: Record<string, unknown> = {};
    lineParentKeys.forEach((k, i) => { snap[k] = watchedParents[i]; });
    return snap;
  }, [lineParentKeys, watchedParents]);

  // Resuelve un form-key contra el merge { ...cabecera, ...línea } (línea precede).
  const resolveCascadeValue = useCallback(
    (formKey: string) => {
      if (lineFieldKeys.has(formKey)) return lineParentSnapshot[formKey];
      return headerValues?.[formKey];
    },
    [lineFieldKeys, lineParentSnapshot, headerValues],
  );

  // Mapa padre → dependientes (sólo dependientes que son campos de la línea).
  const lineDependentsByParent = useMemo(
    () => buildDependentsByParent(lookupConfigs, lineFieldKeys),
    [lookupConfigs, lineFieldKeys],
  );

  // Cambia un campo de línea y limpia los searchers dependientes (sólo en cambios
  // del usuario; la carga inicial usa reset() y no dispara esto).
  const handleLineFieldChange = useCallback(
    (key: string, value: unknown) => {
      setValue(key, value, { shouldDirty: true });
      const dependents = lineDependentsByParent[key];
      if (dependents) {
        dependents.forEach((depKey) => setValue(depKey, '', { shouldDirty: true }));
      }
    },
    [setValue, lineDependentsByParent],
  );

  const visibleFields = useMemo(() => {
    if (conditionDeps.length === 0) return sortedFields;
    return sortedFields.filter((f) => {
      if (f.visibleWhen && !evaluateCondition(f.visibleWhen as FieldCondition, conditionSnapshot)) return false;
      if (f.hiddenWhen && evaluateCondition(f.hiddenWhen as FieldCondition, conditionSnapshot)) return false;
      return true;
    });
  }, [sortedFields, conditionDeps.length, conditionSnapshot]);

  // Sections visible after evaluating their own conditions
  const visibleSections = useMemo<LineModalSectionConfig[]>(() => {
    if (!sections) return [];
    return sections.filter((s) => {
      if (s.visibleWhen && !evaluateCondition(s.visibleWhen as FieldCondition, conditionSnapshot)) return false;
      if (s.hiddenWhen && evaluateCondition(s.hiddenWhen as FieldCondition, conditionSnapshot)) return false;
      return true;
    });
  }, [sections, conditionSnapshot]);

  // Keys of all configured sections (to separate "common" fields that have no matching section)
  const configuredSectionKeys = useMemo(
    () => new Set(sections?.map((s) => s.key) ?? []),
    [sections],
  );

  // Fields above the tabs: fields whose section key isn't configured (always visible in flat mode too)
  const commonFields = useMemo(
    () => (sections ? visibleFields.filter((f) => !configuredSectionKeys.has(f.section)) : []),
    [sections, visibleFields, configuredSectionKeys],
  );

  // Fields for the active tab
  const activeTabFields = useMemo(
    () => (sections ? visibleFields.filter((f) => f.section === activeTab) : visibleFields),
    [sections, visibleFields, activeTab],
  );

  // Keep activeTab pointing at a visible section; fall back to first if current becomes hidden
  useEffect(() => {
    if (!visibleSections.length) return;
    if (!visibleSections.find((s) => s.key === activeTab)) {
      setActiveTab(visibleSections[0].key);
    }
  }, [visibleSections, activeTab]);

  // Reset form and active tab only when modal transitions closed → open.
  // Using a ref to track previous open state prevents resets caused by
  // unstable initialData references (e.g. inline object literals in JSX).
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    const values: Record<string, unknown> = {};
    fields.forEach((f) => {
      values[f.key] = initialData?.[f.key] ?? f.defaultValue ?? '';
    });
    reset(values);
    /*
     * Limpiar lo que marcó el guardián. El modal NO se desmonta al cerrarse —el padre lo
     * monta siempre y solo alterna `open`, y `if (!open) return null` deja el estado
     * vivo—, así que sin esto una línea nueva se abriría con los campos en rojo y los
     * badges del intento anterior.
     */
    setErroresCliente({});

    // Reset active tab to first visible section given initial values
    if (sections && sections.length > 0) {
      const initialSnap: Record<string, unknown> = {};
      fields.forEach((f) => { initialSnap[f.key] = initialData?.[f.key] ?? f.defaultValue ?? ''; });
      const firstVisible = sections.find((s) => {
        if (s.visibleWhen && !evaluateCondition(s.visibleWhen as FieldCondition, initialSnap)) return false;
        if (s.hiddenWhen && evaluateCondition(s.hiddenWhen as FieldCondition, initialSnap)) return false;
        return true;
      });
      setActiveTab(firstVisible?.key ?? sections[0].key);
    }
  }, [open, initialData, fields, reset, sections]);

  // Focus primer campo editable al abrir
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      const first = modalRef.current?.querySelector<HTMLElement>(
        'input:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly])',
      );
      first?.focus();
    }, 80); // espera el fade-in de 150ms para no cortar la animación
    return () => clearTimeout(timer);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // ── Soporte nativo de seriales (FASE 2) ──────────────────
  // Llamado DESPUÉS del efecto de reset para que la re-inyección de `serial_ids`
  // ocurra tras el `reset()` del modal (que solo repuebla las keys de campo).
  const { t: serialT } = useSerialPanelTranslation();
  const serial = useSerialPanel({
    serialSupport,
    control,
    setValue,
    readOnly: readOnly || (serialReadOnly ?? false),
    open,
    initialData,
    validateSerial,
    fetchAvailableSerials,
  });
  const serialBlocksSave = serial.isActive && !serial.isComplete;

  // Computed target fields (readonly). En `useMemo` porque de su identidad dependen
  // `camposQueBloquean` y, por él, el efecto del vigía: recrear el Set en cada render
  // hacía que el efecto se re-ejecutara en cada pulsación de tecla.
  const computedTargets = useMemo(
    () => new Set(computedRules?.map((r) => r.targetField) ?? []),
    [computedRules],
  );

  // Evalúa si un campo es editable según editableOn y el docState actual
  const isFieldEditable = useCallback(
    (field: LineFormModalProps['fields'][number]) => {
      if (readOnly) return false; // modo "Ver": ningún campo es editable
      if (!field.editableOn || field.editableOn.length === 0) return false;
      if (field.editableOn.includes('*')) return true;
      if (!docState) return true; // sin docState → no restringir
      return field.editableOn.includes(docState);
    },
    [readOnly, docState],
  );

  /*
   * ── Guardián de la línea (ADR-027 §C.3) ──────────────────────────────────────
   *
   * Hasta ahora `disabled={serialBlocksSave || busy}` era TODO el control: una línea con
   * un obligatorio vacío se agregaba sin más, porque `validations` no lo leía nadie.
   * Medido en dev el 2026-09-08 sobre una devolución de venta: con «Motivo del
   * movimiento» sin elegir, «Agregar línea» estaba habilitado.
   *
   * Se bloquea al PULSAR, no deshabilitando el botón. Un botón muerto no dice qué falta
   * —y en un formulario con pestañas el campo puede no estar a la vista—, y si el
   * guardián se equivocara sería indiagnosticable. Al pulsar se marca cada campo, se
   * pinta el badge de su pestaña y se salta a la primera con algo pendiente.
   */
  const camposQueBloquean = useMemo(
    // Solo lo que el usuario puede resolver AHORA. Un campo oculto por condición, no
    // editable por estado, sin el permiso de edición (que llega con `editableOn: []`) o
    // calculado no puede bloquear a nadie: no habría forma de desbloquearlo.
    () => visibleFields.filter((f) => isFieldEditable(f) && !computedTargets.has(f.key)),
    [visibleFields, isFieldEditable, computedTargets],
  );

  const calcularBloqueo = useCallback(
    (values: Record<string, unknown>) =>
      evaluateBlockingFields(camposQueBloquean, values, {
        requiredFallbackMessage: t('master_document.lines.fieldRequired'),
      }),
    [camposQueBloquean, t],
  );

  /** `true` si la línea NO puede guardarse; deja ya marcado el motivo. */
  const bloqueaGuardado = useCallback(
    (data: Record<string, unknown>) => {
      const errores = calcularBloqueo(data);
      const claves = Object.keys(errores);
      if (claves.length === 0) {
        setErroresCliente({});
        return false;
      }
      setErroresCliente(errores);
      // Llevar al usuario a la primera pestaña con algo pendiente es la mitad del valor
      // de esta señal: el campo que falta puede estar en otra pestaña.
      const primera = visibleSections.find((sec) =>
        camposQueBloquean.some((f) => f.section === sec.key && claves.includes(f.key)),
      );
      if (primera && primera.key !== activeTab) setActiveTab(primera.key);
      return true;
    },
    [calcularBloqueo, visibleSections, camposQueBloquean, activeTab],
  );

  const handleConfirm = useCallback(
    (data: Record<string, unknown>) => {
      if (bloqueaGuardado(data)) return;
      onConfirm(data);
    },
    [onConfirm, bloqueaGuardado],
  );

  // Guarda la línea actual, limpia el form y deja el modal abierto para agregar otra.
  // Awaita el resultado del parent: si retorna errores, no limpia el form (el banner los muestra).
  const handleConfirmAndAddAnother = useCallback(
    async (data: Record<string, unknown>) => {
      if (bloqueaGuardado(data)) return;
      const errors = (await onConfirmAndAddAnother!(data)) as FieldErrorMap | undefined;
      if (!errors || Object.keys(errors).length === 0) {
        const emptyValues: Record<string, unknown> = {};
        fields.forEach((f) => { emptyValues[f.key] = f.defaultValue ?? ''; });
        reset(emptyValues);
      }
    },
    [onConfirmAndAddAnother, fields, reset, bloqueaGuardado],
  );

  // Form-level errors: keys in externalErrors that don't match any field key
  const fieldKeys = useMemo(() => new Set(fields.map((f) => f.key)), [fields]);
  const formLevelErrors = useMemo(
    () =>
      externalErrors
        ? Object.entries(externalErrors)
            .filter(([key]) => !fieldKeys.has(key))
            .map(([, err]) => err)
        : [],
    [externalErrors, fieldKeys],
  );


  const gridClass = columns === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2';

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={clsx(
          // Desktop: centered modal
          'fixed z-[301] bg-bg-primary rounded-2xl overflow-hidden',
          'shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          // Desktop
          'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-h-[85vh]',
          widgetSlot ? 'max-w-[1000px]' : 'max-w-[700px]',
          // Mobile: bottom sheet
          'max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:translate-x-0 max-sm:translate-y-0',
          'max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:max-h-[90vh]',
          'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
          className,
        )}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-secondary dark:border-dark-border-primary">
          <h2 className="text-base font-bold text-content-primary dark:text-dark-content-primary">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-background-secondary text-content-secondary transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary dark:hover:bg-dark-bg-secondary"
            aria-label={t('master_document.chrome.close')}
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body: form + optional widget sidebar */}
        <div
          className={clsx('flex', widgetSlot ? 'flex-row' : 'flex-col')}
          style={{ maxHeight: 'calc(85vh - 130px)' }}
        >
          {/* Form body */}
          {/* El cuerpo ya NO scrollea como un todo: la barra de pestañas vivía dentro
              del scroller, así que en una pestaña larga —«Costo», con seis campos y
              los paneles de serie— se iba hacia arriba con el contenido y el usuario
              perdía de vista en cuál estaba y cómo cambiar. Aquí sólo se reparte el
              alto; quien scrollea es la caja de campos de abajo. */}
          <div className={clsx('flex flex-col overflow-hidden min-h-0 px-6 py-5', widgetSlot ? 'flex-1 min-w-0' : 'w-full flex-1')}>
            {/* Computed rules runner */}
            {computedRules && computedRules.length > 0 && (
              <ComputedRulesRunner rules={computedRules} control={control} setValue={setValue} />
            )}

            {/* Server rules runner */}
            {serverRules && serverRules.length > 0 && onEvaluateServerRule && (
              <ServerRulesRunner
                rules={serverRules}
                control={control}
                setValue={setValue}
                onEvaluate={onEvaluateServerRule}
                setLoadingFields={setLoadingFields}
              />
            )}

            {/* Form values watcher — notifies parent when values change */}
            {onFormValuesChange && (
              <FormValuesWatcher control={control} onChange={onFormValuesChange} />
            )}

            {/* Form-level error banner — errors whose keys don't match any field */}
            {formLevelErrors.length > 0 && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-950/20"
              >
                {formLevelErrors.map((err, i) => (
                  <p key={i} className="text-sm text-red-700 dark:text-red-400">
                    {err.message}
                  </p>
                ))}
              </div>
            )}

            {/* Common fields — rendered above tabs (section key not listed in sections config) */}
            {sections && commonFields.length > 0 && (
              <div className={clsx('grid gap-4 mb-4', gridClass)}>
                {commonFields.map((field) => {
                  const isComputed = computedTargets.has(field.key);
                  const isLoading = loadingFields.has(field.key);
                  const editable = isFieldEditable(field) && !isComputed && !isLoading;
                  const depProps = buildDependentLookupProps(lookupConfigs?.[field.key], resolveCascadeValue);
                  return (
                    <div key={field.key} className={clsx(field.span >= 2 && columns === 2 && 'sm:col-span-2')}>
                      <FieldRendererControlled
                        field={field}
                        control={control}
                        setValue={setValue}
                        onValueChange={handleLineFieldChange}
                        editable={editable}
                        readOnly={isComputed}
                        loading={isLoading}
                        error={externalErrors?.[field.key] ?? erroresCliente[field.key]}
                        isComputed={isComputed}
                        lookupConfig={lookupConfigs?.[field.key]}
                        fetcher={fetcher}
                        dependentProps={depProps}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Solo tras un intento fallido: antes no observa nada. */}
            {huboIntentoFallido && (
              <LineBlockingWatcher
                control={control}
                calcular={calcularBloqueo}
                onChange={setErroresCliente}
              />
            )}

            {/*
              Tab bar. El asterisco sale de la obligatoriedad declarada y se ve desde el
              primer render; el badge cuenta los campos que están bloqueando y solo
              aparece tras un intento fallido. Las dos marcas salen de la MISMA fuente que
              el guardián, así que no puede haber una pestaña con número y sin asterisco
              —la contradicción del §C.3b—. Las clases son las literales de `CanonTabsBar`
              para que la marca se lea igual aquí que en las pestañas de la cabecera.
            */}
            {visibleSections.length > 0 && (
              <div className="shrink-0 flex gap-1 pt-0.5 pb-3 mb-4 px-0.5 border-b border-border-secondary dark:border-dark-border-primary overflow-x-auto">
                {visibleSections.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => setActiveTab(section.key)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-1',
                      activeTab === section.key
                        ? 'bg-primary-inverse-background text-primary-inverse-content dark:bg-dark-bg-inverse dark:text-dark-content-inverse'
                        : 'text-content-secondary hover:bg-background-secondary hover:text-content-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-content-primary',
                    )}
                  >
                    {section.label}
                    {/* La MISMA regla que las pestañas del encabezado (§C.3b): la marca
                        sólo sale si dentro hay un obligatorio que el usuario pueda ver
                        marcado. `FieldRenderer` pinta el asterisco únicamente en campos
                        editables, así que en un modal de sólo lectura —o sobre un campo
                        `readonly`/`display`— la pestaña anunciaba un obligatorio que su
                        contenido no mostraba, y que además nadie podía rellenar. Medido
                        en dev el 2026-09-09: el modal de línea de una devolución
                        aprobada, sin un solo control editable, traía `*` en tres de sus
                        cuatro pestañas. */}
                    {!readOnly
                      && visibleFields.some(
                        (f) => f.section === section.key && f.required
                          && f.control !== 'readonly' && f.control !== 'display',
                      ) && <span className="text-[#dc2626]">*</span>}
                    {(() => {
                      const faltan = camposQueBloquean.filter(
                        (f) => f.section === section.key && erroresCliente[f.key] !== undefined,
                      ).length;
                      return faltan > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 bg-[#dc2626] text-white rounded-full text-[9px] font-bold">
                          {faltan}
                        </span>
                      ) : null;
                    })()}
                  </button>
                ))}
              </div>
            )}

            {/* Scroller del contenido de la pestaña. `min-h-0` es lo que permite que un
                hijo de flex se encoja por debajo de su contenido; sin él el `flex-1`
                crece hasta el alto natural del formulario y no scrollea nada.

                El `-mx-2 px-2` es SIMÉTRICO a propósito. Un contenedor con
                `overflow-y-auto` recorta también en el eje horizontal (CSS lo obliga:
                con un `overflow` distinto de `visible` en un eje, el otro no puede
                quedarse en `visible`), y el anillo de foco del canon sobresale ~4px del
                input —`ring-2` más `ring-offset-2`—. Sin holgura a los dos lados, un
                campo pegado al borde se enfoca y su recuadro azul aparece cortado a la
                izquierda, a la derecha o en ambas. Antes sólo había `pr-2`, que tapaba
                el lado derecho y dejaba el izquierdo a la vista. El margen negativo
                repone el gutter, así que los campos no se mueven. */}
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin -mx-2 px-2">
            <div className={clsx('grid gap-4', gridClass)}>
              {activeTabFields.map((field) => {
                const isComputed = computedTargets.has(field.key);
                const isLoading = loadingFields.has(field.key);
                const editable = isFieldEditable(field) && !isComputed && !isLoading;
                const depProps = buildDependentLookupProps(lookupConfigs?.[field.key], resolveCascadeValue);

                return (
                  <div
                    key={field.key}
                    className={clsx(
                      field.span >= 2 && columns === 2 && 'sm:col-span-2',
                    )}
                  >
                    <FieldRendererControlled
                      field={field}
                      control={control}
                      setValue={setValue}
                      onValueChange={handleLineFieldChange}
                      editable={editable}
                      readOnly={isComputed}
                      loading={isLoading}
                      error={externalErrors?.[field.key] ?? erroresCliente[field.key]}
                      isComputed={isComputed}
                      lookupConfig={lookupConfigs?.[field.key]}
                      fetcher={fetcher}
                      dependentProps={depProps}
                    />
                  </div>
                );
              })}
            </div>

            {/* Soporte nativo de seriales — panel cableado (FASE 2) */}
            {serial.isActive && serial.panelProps && (
              <div className="mt-5 pt-5 border-t border-border-secondary dark:border-dark-border-primary">
                <h3 className="mb-3 text-sm font-bold text-content-primary dark:text-dark-content-primary">
                  {serialT('serial_panel.section_title')}
                </h3>
                <SerialPanel {...serial.panelProps} />
              </div>
            )}

            {/* Campos adicionales (EAV) de la línea. Al final del cuerpo del
                formulario, después de los seriales: son campos de captura, así
                que van en la columna que sí existe en móvil — no en el panel
                lateral `widgetSlot`, que está oculto en pantallas pequeñas. */}
            {dynamicEntitiesSlot && (
              <div className="mt-5 border-t border-border-secondary pt-5 dark:border-dark-border-primary">
                {dynamicEntitiesSlot}
              </div>
            )}

            {/* Degradación: serialSupport activo pero el adapter no implementa el método */}
            {serial.unsupported && serial.unsupportedMessage && (
              <div
                role="alert"
                className="mt-5 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3 text-[13px] text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
              >
                <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0" />
                <span>{serial.unsupportedMessage}</span>
              </div>
            )}
            </div>
          </div>

          {/* Widget sidebar */}
          {widgetSlot && (
            <div className="w-72 shrink-0 border-l border-border-secondary dark:border-dark-border-primary overflow-y-auto max-sm:hidden">
              {widgetSlot}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-secondary dark:border-dark-border-primary">
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              // Secundario del canon: LLEVA borde. Sin él era un texto suelto que no se
              // leía como botón —el «Cancelar» de MasterCrud y los otros modales del
              // propio kit (`ConflictModal`, `LineCloseModal`, `CrossingGenerateModal`)
              // ya lo pintan con `border border-border-primary`.
              'px-4 py-2 rounded-md text-sm font-semibold',
              'border border-border-primary dark:border-dark-border-primary',
              'text-content-tertiary hover:bg-background-secondary transition-all duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
            )}
          >
            {resolvedCancelLabel}
          </button>

          {/* Guardar y agregar otra — solo visible cuando se provee el callback */}
          {!readOnly && onConfirmAndAddAnother && (
            <button
              type="button"
              onClick={handleSubmit(handleConfirmAndAddAnother)}
              disabled={serialBlocksSave || busy}
              title={serialBlocksSave ? serialT('serial_panel.save_tooltip', { count: Math.max(0, serial.required - serial.count) }) : undefined}
              className={clsx(
                'px-4 py-2 rounded-md text-sm font-semibold',
                'border border-border-primary text-content-primary hover:bg-background-secondary transition-all duration-150',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
                'dark:border-dark-border-primary dark:text-dark-content-primary dark:hover:bg-dark-bg-secondary',
                'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent',
              )}
            >
              {t('master_document.lines.saveAndAddAnother')}
            </button>
          )}

          {/* En modo solo-lectura ("Ver") se ocultan los botones de confirmación. */}
          {!readOnly && (
            <button
              type="button"
              onClick={handleSubmit(handleConfirm)}
              disabled={serialBlocksSave || busy}
              title={serialBlocksSave ? serialT('serial_panel.save_tooltip', { count: Math.max(0, serial.required - serial.count) }) : undefined}
              className={clsx(
                'px-5 py-2 rounded-md text-sm font-semibold',
                'bg-primary-inverse-background text-primary-inverse-content hover:bg-primary-700 transition-all duration-150',
                'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
                'min-h-[36px]',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
                'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-inverse-background',
              )}
            >
              {resolvedConfirmLabel}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Controlled Field Wrapper ──────────────────────────────

function FieldRendererControlled({
  field,
  control,
  setValue,
  onValueChange,
  editable,
  readOnly,
  loading,
  error,
  isComputed,
  lookupConfig,
  fetcher,
  dependentProps,
}: {
  field: LineFormModalProps['fields'][number];
  control: ReturnType<typeof useForm>['control'];
  setValue: ReturnType<typeof useForm>['setValue'];
  onValueChange: (key: string, value: unknown) => void;
  editable: boolean;
  readOnly: boolean;
  loading?: boolean;
  error?: import('./MasterDocument.types').FieldError;
  isComputed: boolean;
  lookupConfig?: LookupFieldConfig;
  fetcher?: unknown;
  dependentProps?: DependentLookupProps;
}) {
  const { t } = useMasterDocumentTranslation();
  const value = useWatch({ control, name: field.key });

  const handleRecordSelect = useCallback(
    (record: Record<string, unknown> | null) => {
      if (!record || !lookupConfig?.bindFields) return;
      for (const [targetField, sourceField] of Object.entries(lookupConfig.bindFields)) {
        const bound = record[sourceField];
        if (bound !== undefined) setValue(targetField, bound, { shouldDirty: true });
      }
    },
    [lookupConfig, setValue],
  );

  return (
    <div className="relative">
      <FieldRenderer
        fieldDef={field}
        value={value}
        onChange={(v) => onValueChange(field.key, v)}
        editable={editable}
        readOnly={readOnly}
        error={error}
        lookupConfig={lookupConfig}
        fetcher={fetcher}
        onRecordSelect={handleRecordSelect}
        getLookupFilters={dependentProps?.getFilters}
        lookupDependencies={dependentProps?.dependencies}
        disabled={dependentProps?.parentMissing}
      />
      {isComputed && (
        <span className="text-xs text-content-secondary dark:text-dark-content-secondary italic mt-0.5">{t('master_document.lines.calculated')}</span>
      )}
      {loading && <FieldLoadingIndicator />}
    </div>
  );
}
